import { List } from '@gonfalon/immutable-shim/immutableShim';
import { stringifyValue } from '@gonfalon/types/stringifyValue';

import type { Flag } from '#utils/flags/records/Flag';
import type { Variation } from '#utils/flags/records/Variation';
import { InstructionCategory } from '#utils/instructions/shared/constants';
import type { InstructionsType, SemanticInstruction } from '#utils/instructions/shared/types';
import type { UserSettings } from '#utils/userUtils';

import type {
  AddTargetsSemanticInstruction,
  RemoveTargetsSemanticInstruction,
  ReplaceTargetsSemanticInstruction,
  UpdateTargetsSemanticInstruction,
} from './types';
import { TargetsInstructionKind } from './types';

export function makeAddTargetsInstruction(
  targetKeys: string[],
  variationId: string,
  contextKind: string,
): AddTargetsSemanticInstruction {
  return {
    kind: TargetsInstructionKind.ADD_TARGETS,
    contextKind,
    values: targetKeys,
    variationId,
  };
}

export function makeRemoveTargetsInstruction(
  targetKeys: string[],
  variationId: string,
  contextKind: string,
): RemoveTargetsSemanticInstruction {
  return {
    kind: TargetsInstructionKind.REMOVE_TARGETS,
    contextKind,
    values: targetKeys,
    variationId,
  };
}

export function sortTargetInstructions(instructions: List<SemanticInstruction> | undefined): List<SemanticInstruction> {
  if (!instructions) {
    return List();
  }
  const removeTargets = instructions.filter((i) => i.kind === TargetsInstructionKind.REMOVE_TARGETS);
  const addTargets = instructions.filter((i) => i.kind === TargetsInstructionKind.ADD_TARGETS);
  return removeTargets.concat(addTargets);
}

/**
 * Sets the add or remove target instruction for this (variationId, contextKind).
 * Net add/remove lists are already computed by getInstructionsForTargets, so we only store the instruction.
 */
export function combineTargetsInstructions(
  newInstruction: UpdateTargetsSemanticInstruction,
  pendingSemanticPatch: InstructionsType,
): InstructionsType {
  const key = makeUpdateTargetsKey(newInstruction.variationId, newInstruction.kind, newInstruction.contextKind);
  return pendingSemanticPatch.set(key, newInstruction);
}

export function makeUpdateTargetsKey(
  variationId: string,
  instructionKind: TargetsInstructionKind,
  contextKind: string,
): string {
  return [InstructionCategory.TARGETS, variationId, instructionKind, contextKind].join('|');
}

const makeRemoveContextDetailTargetInstruction = (ContextKeys: string[], variationId: string, contextKind: string) =>
  makeRemoveTargetsInstruction(ContextKeys ?? [], variationId, contextKind);

const makeAddContextDetailTargetInstruction = (ContextKeys: string[], variationId: string, contextKind: string) =>
  makeAddTargetsInstruction(ContextKeys ?? [], variationId, contextKind);

export const getTargetsInstructionsFromContextsDetails = (
  contextKey: string,
  flag: Flag,
  originalUserSetting: UserSettings,
  contextKind: string,
  targetVariation?: Variation,
) => {
  const originalVariationValue = stringifyValue(originalUserSetting._value);
  let isOriginalValueDefault = true;
  const newVariationValue = targetVariation ? stringifyValue(targetVariation.value) : null;
  if (originalVariationValue !== newVariationValue) {
    isOriginalValueDefault = false;
  }
  if (originalVariationValue === newVariationValue && !isOriginalValueDefault) {
    return [];
  }

  const shouldAddToNewVariation = newVariationValue !== null;
  const shouldRemoveFromCurrVariation =
    (newVariationValue === null && !isOriginalValueDefault) || (shouldAddToNewVariation && !isOriginalValueDefault);

  const instructions = [];
  if (shouldRemoveFromCurrVariation) {
    const currVariationId = flag.variations.find((v) => stringifyValue(v.value) === originalVariationValue)?._id;
    if (currVariationId) {
      instructions.push(makeRemoveContextDetailTargetInstruction([contextKey], currVariationId, contextKind));
    }
  }
  if (shouldAddToNewVariation) {
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    instructions.push(
      makeAddContextDetailTargetInstruction([contextKey], targetVariation!._id, contextKind),
    ); /* eslint-enable @typescript-eslint/no-non-null-assertion */
  }
  return instructions;
};

export function makeReplaceTargetsInstruction(
  targets: Array<{ variationId: string; values: string[]; contextKind: string }>,
  contextKind: string,
): ReplaceTargetsSemanticInstruction {
  return {
    kind: TargetsInstructionKind.REPLACE_TARGETS,
    targets,
    contextKind,
  };
}

export function hasEmptyTargets(instruction: ReplaceTargetsSemanticInstruction) {
  return instruction.targets.every((v) => v.values.length === 0);
}
